/**
 * Backoffice CRM: Waitlist & Recipe Rescue Leads Controller
 */

import { setNavBadge } from "./ui/shell.js";
import { BADGE, BUTTON } from "./ui/classes.js";
import { platformLogo, solidIcon } from "./ui/icons.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { copyAttributes } from "./ui/copy.js";
import { safeUrl } from "./familyDetailsPanel.js";
import { offerText } from "./utils/subscriptionOffer.js";
import { countOfferStatuses, matchesOfferFilter, offerStatusBadgeHTML, offerStatusCellHTML, offerStatusRows, wasHandedOffer } from "./utils/leadOfferStatus.js";

let leadsList = [];
let firestoreCtx = null;
let activeSearchTerm = "";
let activeSourceFilter = "ALL";
let activeRecipeFilter = "ALL";
let activeClaimFilter = "ALL";
let activePhoneFilter = "ALL";
let activeNewsletterFilter = "ALL";
let activeOfferFilter = "ALL";
let selectedLeadItem = null;

// Pagination & Caching state
const LEADS_PAGE_SIZE = 50;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes TTL
let currentLeadsPage = 1;
let lastLeadsFetchTime = 0;
let lastPromoFetchTime = 0;
let totalLeadsCount = 0;
let totalRescueLeadsCount = 0;
let totalClaimedLeadsCount = 0;
let isFetchingLeads = false;
let isFetchingPromo = false;

/**
 * Returns true if a waitlist lead has been claimed (fully redeemed or promo assigned)
 */
export function isLeadClaimed(lead) {
  return lead.status === "claimed" || Boolean(lead.claimed_at) || Boolean(lead.claimed_by_family_id) || Boolean(lead.promo_code) || Boolean(lead.promo_claimed_at);
}

/**
 * Returns formatted HTML for the Claim & Promo Status cell in leads table
 */
export function getLeadClaimStatusBadgeHTML(lead) {
  const claimed = isLeadClaimed(lead);
  const platform = (lead.promo_platform || "").toLowerCase();
  const platformLabel = platform === "ios" ? "iOS" : platform === "android" ? "Android" : "";

  let promoHTML = "";
  if (lead.promo_code) {
    const code = escapeHTML(lead.promo_code);
    promoHTML = `
      <span title="Promo code${platformLabel ? ` (${platformLabel})` : ""}: ${code}" class="${BADGE.green} font-mono">
        ${code}
        <button type="button" ${copyAttributes(lead.promo_code, "Copied the promo code.")} title="Copy the promo code" class="-mr-1 rounded-xs p-0.5 hover:bg-green-600/20"><span class="sr-only">Copy</span>${solidIcon("clipboard-document", "size-3.5")}</button>
      </span>`;
  } else if (lead.promo_status === "exhausted") {
    promoHTML = `<span class="${BADGE.yellow}">${solidIcon("exclamation-triangle", "size-3.5")}Promo codes ran out</span>`;
  }

  // A lead with an account but no claim, and a lead who unsubscribed, are left out of follow-ups (#2308).
  const extraHTML = [
    !claimed && getLeadAccountEvidence(lead).length ? `<span class="${BADGE.green}">Has the app</span>` : "",
    isLeadUnsubscribed(lead) ? `<span class="${BADGE.red}">Unsubscribed</span>` : ""
  ].join("");

  if (!claimed) {
    return `<div class="flex flex-col items-start gap-1"><span class="${BADGE.gray}">${solidIcon("clock", "size-3.5")}Unclaimed</span>${promoHTML}${extraHTML}</div>`;
  }

  const claimDate = formatLeadDate(lead.claimed_at || lead.promo_claimed_at);
  const isFullyClaimed = lead.status === "claimed" || Boolean(lead.claimed_by_family_id) || Boolean(lead.claimed_at);
  const statusBadge = isFullyClaimed
    ? `<span class="${BADGE.green}">${solidIcon("check-circle", "size-3.5")}Claimed</span>`
    : `<span class="${BADGE.blue}">${solidIcon("ticket", "size-3.5")}Promo allocated</span>`;

  return `
    <div class="flex flex-col items-start gap-1">
      ${statusBadge}
      ${promoHTML || `<span class="text-xs text-gray-500 dark:text-gray-400">No promo code</span>`}
      <span class="text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">Claimed ${escapeHTML(claimDate)}</span>
      ${extraHTML}
    </div>
  `;
}

/**
 * Generates a 16-character hex claim token on the client/**
 * Generates a 16-character hex claim token on the client
 */
export function generateClientClaimToken() {
  if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  let res = "";
  for (let i = 0; i < 16; i++) {
    res += Math.floor(Math.random() * 16).toString(16);
  }
  return res;
}

/**
 * Returns the destination claim URL for a lead to paste into ChottuLink
 */
export function getLeadDestinationUrl(lead) {
  if (lead.claim_url) return lead.claim_url;
  const token = (lead.claim_token || "").trim();
  if (!token) return "";
  const count = Number(lead.recipe_count) || (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0);
  return `https://weeknightbiteapp.com/claim?token=${encodeURIComponent(token)}&count=${count}`;
}

/**
 * Returns active link (ChottuLink if available, else universal app link or web claim link)
 */
export function getLeadActiveClaimUrl(lead) {
  if (lead.dynamic_link) return lead.dynamic_link;
  if (lead.app_claim_url && !lead.app_claim_url.includes("/claim")) return lead.app_claim_url;
  return getLeadDestinationUrl(lead);
}

/**
 * Loads the leads.html partial template into the container
 */
export async function loadLeadsTemplate() {
  const container = document.getElementById("sectionLeadsContainer");
  if (!container) return;

  try {
    const res = await fetch("templates/leads.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load leads.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the leads template: ${escapeHTML(err.message)}</div>`;
  }
}

/**
 * Initializes the leads controller with Firestore bindings
 */
export function initLeadsController(ctx) {
  firestoreCtx = ctx;

  const searchInput = document.getElementById("leadsSearchInput");
  const sourceFilter = document.getElementById("leadsSourceFilter");
  const recipeFilter = document.getElementById("leadsRecipeFilter");
  const copyEmailsBtn = document.getElementById("copyLeadsEmailsBtn");
  const exportCsvBtn = document.getElementById("exportLeadsCsvBtn");
  const refreshBtn = document.getElementById("refreshLeadsBtn");

  // Drawer elements
  const modal = document.getElementById("leadDetailModal");
  const modalCopyEmailBtn = document.getElementById("modalCopyEmailBtn");
  const modalCopyRecipeUrlsBtn = document.getElementById("modalCopyRecipeUrlsBtn");
  const modalToggleRawJsonBtn = document.getElementById("modalToggleRawJsonBtn");
  const modalCopyRawJsonBtn = document.getElementById("modalCopyRawJsonBtn");

  if (searchInput) {
    searchInput.addEventListener("input", (e) => {
      activeSearchTerm = e.target.value.trim().toLowerCase();
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  if (sourceFilter) {
    sourceFilter.addEventListener("change", (e) => {
      activeSourceFilter = e.target.value;
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  if (recipeFilter) {
    recipeFilter.addEventListener("change", (e) => {
      activeRecipeFilter = e.target.value;
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  const phoneFilter = document.getElementById("leadsPhoneFilter");
  if (phoneFilter) {
    phoneFilter.addEventListener("change", (e) => {
      activePhoneFilter = e.target.value;
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  const newsletterFilter = document.getElementById("leadsNewsletterFilter");
  if (newsletterFilter) {
    newsletterFilter.addEventListener("change", (e) => {
      activeNewsletterFilter = e.target.value;
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  // The 50% offer (#1960): the dropdown, and the stat cards that set it
  const offerFilter = document.getElementById("leadsOfferFilter");
  const setOfferFilter = (value) => {
    activeOfferFilter = value;
    if (offerFilter) offerFilter.value = value;
    currentLeadsPage = 1;
    renderLeadsTable();
  };
  if (offerFilter) offerFilter.addEventListener("change", (e) => setOfferFilter(e.target.value));
  document.querySelectorAll("#sectionLeads [data-offer-filter]").forEach((card) => {
    card.addEventListener("click", () => setOfferFilter(card.dataset.offerFilter));
  });

  const claimFilter = document.getElementById("leadsClaimFilter");
  if (claimFilter) {
    claimFilter.addEventListener("change", (e) => {
      activeClaimFilter = e.target.value;
      currentLeadsPage = 1;
      renderLeadsTable();
    });
  }

  // Pagination buttons
  const prevPageBtn = document.getElementById("prevLeadsPageBtn");
  const nextPageBtn = document.getElementById("nextLeadsPageBtn");

  if (prevPageBtn) {
    prevPageBtn.addEventListener("click", () => {
      if (currentLeadsPage > 1) {
        currentLeadsPage--;
        renderLeadsTable();
      }
    });
  }

  if (nextPageBtn) {
    nextPageBtn.addEventListener("click", () => {
      currentLeadsPage++;
      renderLeadsTable();
    });
  }

  if (copyEmailsBtn) {
    copyEmailsBtn.addEventListener("click", () => copyFilteredEmails());
  }

  if (exportCsvBtn) {
    exportCsvBtn.addEventListener("click", () => exportLeadsCSV());
  }

  if (refreshBtn) {
    refreshBtn.addEventListener("click", async () => {
      await fetchLeadsList(true);
    });
  }

  // The drawer's close buttons, Esc and the backdrop all close the <dialog> (el-dialog).
  if (modal) modal.addEventListener("close", () => { selectedLeadItem = null; });

  if (modalCopyEmailBtn) {
    modalCopyEmailBtn.addEventListener("click", () => {
      if (!selectedLeadItem || !selectedLeadItem.email) return;
      navigator.clipboard.writeText(selectedLeadItem.email);
      firestoreCtx?.showToast?.("Lead email copied to clipboard!", "info");
    });
  }

  if (modalCopyRecipeUrlsBtn) {
    modalCopyRecipeUrlsBtn.addEventListener("click", () => {
      if (!selectedLeadItem) return;
      const urls = Array.isArray(selectedLeadItem.recipe_urls) ? selectedLeadItem.recipe_urls : [];
      if (urls.length === 0) return;
      navigator.clipboard.writeText(urls.join("\n"));
      firestoreCtx?.showToast?.(`Copied ${urls.length} recipe URL(s) to clipboard!`, "success");
    });
  }

  if (modalToggleRawJsonBtn) {
    modalToggleRawJsonBtn.addEventListener("click", () => {
      const pre = document.getElementById("modalLeadRawJson");
      const copyBtn = document.getElementById("modalCopyRawJsonBtn");
      if (!pre) return;
      const isHidden = pre.style.display === "none";
      pre.style.display = isHidden ? "block" : "none";
      if (copyBtn) copyBtn.style.display = isHidden ? "inline-flex" : "none";
      modalToggleRawJsonBtn.setAttribute("aria-expanded", String(isHidden));
      const label = modalToggleRawJsonBtn.querySelector("[data-label]");
      if (label) label.textContent = isHidden ? "Hide the raw document" : "Raw document";
    });
  }

  if (modalCopyRawJsonBtn) {
    modalCopyRawJsonBtn.addEventListener("click", () => {
      if (!selectedLeadItem) return;
      navigator.clipboard.writeText(JSON.stringify(selectedLeadItem, null, 2));
      firestoreCtx?.showToast?.("Raw payload copied to clipboard!", "info");
    });
  }

  // Promo Inventory elements
  document.getElementById("checkAndroidOffersBtn")?.addEventListener("click", () => checkAndroidOffers());
  document.getElementById("reengageCountBtn")?.addEventListener("click", () => countReengage());
  document.getElementById("reengageSendBtn")?.addEventListener("click", () => sendReengage());
  // A count belongs to its segment: another segment needs its own count before Send.
  document.getElementById("reengageSegmentSelect")?.addEventListener("change", () => resetReengage());

  const refreshPromoStockBtn = document.getElementById("refreshPromoStockBtn");
  const uploadPromoCsvBtn = document.getElementById("uploadPromoCsvBtn");

  if (refreshPromoStockBtn) {
    refreshPromoStockBtn.addEventListener("click", async () => {
      await fetchPromoInventoryStock(true);
    });
  }

  if (uploadPromoCsvBtn) {
    uploadPromoCsvBtn.addEventListener("click", async () => {
      await handlePromoCsvUpload();
    });
  }

  const togglePromoManageBtn = document.getElementById("togglePromoManageBtn");
  const promoManagePanel = document.getElementById("promoManagePanel");
  if (togglePromoManageBtn && promoManagePanel) {
    togglePromoManageBtn.addEventListener("click", () => {
      const open = promoManagePanel.style.display === "none";
      promoManagePanel.style.display = open ? "block" : "none";
      togglePromoManageBtn.setAttribute("aria-expanded", String(open));
      const label = togglePromoManageBtn.querySelector("[data-label]");
      if (label) label.textContent = open ? "Hide codes" : "Manage codes";
    });
  }
}

/**
 * Fetches promo code inventory and updates UI counts using getCountFromServer (4 reads total)
 */
export async function fetchPromoInventoryStock(force = false) {
  if (!firestoreCtx) return;
  const { db, collection, getCountFromServer, query, where, showToast } = firestoreCtx;

  const now = Date.now();
  if (!force && lastPromoFetchTime > 0 && now - lastPromoFetchTime < CACHE_TTL_MS) {
    return; // Re-use warm cache
  }

  if (isFetchingPromo) return;
  isFetchingPromo = true;

  try {
    const promoCol = collection(db, "promo_code_inventory");

    let iosAvailable = 0;
    let iosClaimed = 0;
    let androidAvailable = 0;
    let androidClaimed = 0;

    if (typeof getCountFromServer === "function" && typeof query === "function" && typeof where === "function") {
      // 4 targeted count queries (1 doc read each = 4 reads total)
      const [iosAvailSnap, iosClaimSnap, androidAvailSnap, androidClaimSnap] = await Promise.all([
        getCountFromServer(query(promoCol, where("platform", "==", "ios"), where("status", "==", "available"))),
        getCountFromServer(query(promoCol, where("platform", "==", "ios"), where("status", "==", "claimed"))),
        getCountFromServer(query(promoCol, where("platform", "==", "android"), where("status", "==", "available"))),
        getCountFromServer(query(promoCol, where("platform", "==", "android"), where("status", "==", "claimed")))
      ]);

      iosAvailable = iosAvailSnap.data().count || 0;
      iosClaimed = iosClaimSnap.data().count || 0;
      androidAvailable = androidAvailSnap.data().count || 0;
      androidClaimed = androidClaimSnap.data().count || 0;
    } else {
      // Fallback only if getCountFromServer is unavailable
      const { getDocs } = firestoreCtx;
      const snap = await getDocs(promoCol);
      snap.forEach((d) => {
        const data = d.data() || {};
        const platform = (data.platform || "").toLowerCase();
        const status = (data.status || "").toLowerCase();

        if (platform === "ios") {
          if (status === "available") iosAvailable++;
          else if (status === "claimed") iosClaimed++;
        } else if (platform === "android") {
          if (status === "available") androidAvailable++;
          else if (status === "claimed") androidClaimed++;
        }
      });
    }

    lastPromoFetchTime = Date.now();

    const iosTotal = iosAvailable + iosClaimed;
    const androidTotal = androidAvailable + androidClaimed;

    const elIosAvailable = document.getElementById("promoIosAvailableCount");
    const elIosClaimed = document.getElementById("promoIosClaimedCount");
    const elIosTotalBadge = document.getElementById("promoIosTotalBadge");

    const elAndroidAvailable = document.getElementById("promoAndroidAvailableCount");
    const elAndroidClaimed = document.getElementById("promoAndroidClaimedCount");
    const elAndroidTotalBadge = document.getElementById("promoAndroidTotalBadge");

    if (elIosAvailable) elIosAvailable.textContent = String(iosAvailable);
    if (elIosClaimed) elIosClaimed.textContent = String(iosClaimed);
    if (elIosTotalBadge) elIosTotalBadge.textContent = `${iosTotal} Total`;

    if (elAndroidAvailable) elAndroidAvailable.textContent = String(androidAvailable);
    if (elAndroidClaimed) elAndroidClaimed.textContent = String(androidClaimed);
    if (elAndroidTotalBadge) elAndroidTotalBadge.textContent = `${androidTotal} Total`;

    // The one-line summary shown while "Manage codes" is closed; amber when a store has run out
    for (const [id, available] of [["promoIosSummary", iosAvailable], ["promoAndroidSummary", androidAvailable]]) {
      const el = document.getElementById(id);
      if (!el) continue;
      el.textContent = String(available);
      el.toggleAttribute?.("data-empty", available === 0);
    }
  } catch (err) {
    console.error("❌ Failed to fetch promo code inventory stock:", err);
    showToast?.(`Failed to load promo stock: ${err.message}`, "error");
  } finally {
    isFetchingPromo = false;
  }
}

/**
 * Handles CSV upload and imports promo codes into promo_code_inventory
 */
export async function handlePromoCsvUpload() {
  if (!firestoreCtx) return;
  const { db, collection, doc, writeBatch, getDocs, showToast } = firestoreCtx;

  const fileInput = document.getElementById("promoCsvFileInput");
  const platformSelect = document.getElementById("promoPlatformSelect");
  const campaignInput = document.getElementById("promoCampaignInput");
  const uploadBtn = document.getElementById("uploadPromoCsvBtn");
  const statusEl = document.getElementById("promoUploadStatus");

  // data-tone colours the status line: info, error or success.
  const showStatus = (msg, type = "info") => {
    if (!statusEl) return;
    statusEl.style.display = "block";
    statusEl.dataset.tone = type;
    statusEl.textContent = msg;
  };
  const uploadLabel = uploadBtn ? uploadBtn.innerHTML : "";
  const restoreUploadBtn = () => {
    if (!uploadBtn) return;
    uploadBtn.disabled = false;
    uploadBtn.innerHTML = uploadLabel;
  };

  if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
    showToast?.("Please choose a CSV or TXT file to upload.", "warning");
    showStatus("Please choose a CSV or TXT file to upload.", "error");
    return;
  }

  const file = fileInput.files[0];
  const platform = (platformSelect?.value || "ios").trim().toLowerCase();
  const campaign = (campaignInput?.value || "prelaunch_50").trim() || "prelaunch_50";

  if (platform !== "ios" && platform !== "android") {
    showToast?.("Invalid platform selected. Choose iOS or Android.", "error");
    return;
  }

  try {
    if (uploadBtn) {
      uploadBtn.disabled = true;
      uploadBtn.textContent = "Uploading…";
    }
    showStatus(`Reading file ${file.name}...`, "info");

    const text = await file.text();
    const lines = text.split(/\r?\n/);
    const parsedCodes = [];
    const headerPattern = /^(code|promo|promo_code|promocode|promo\s*code|promo\s*codes|codes)$/i;

    for (const rawLine of lines) {
      let line = rawLine.trim();
      if (!line) continue;
      // Strip surrounding quotes or commas if CSV
      line = line.replace(/^["']|["']$/g, "").trim();
      if (line.includes(",")) {
        // If multi-column CSV, take the first column
        line = line.split(",")[0].replace(/^["']|["']$/g, "").trim();
      }
      if (!line) continue;
      if (headerPattern.test(line)) continue;
      parsedCodes.push(line);
    }

    // Deduplicate within file
    const uniqueCodes = Array.from(new Set(parsedCodes));
    if (uniqueCodes.length === 0) {
      showToast?.("No valid promo codes found in the selected file.", "warning");
      showStatus("No valid promo codes found in the selected file.", "error");
      restoreUploadBtn();
      return;
    }

    showStatus(`Found ${uniqueCodes.length} codes in file. Checking existing inventory...`, "info");

    // Fetch existing keys to prevent overwriting claimed or existing codes
    const existingSnap = await getDocs(collection(db, "promo_code_inventory"));
    const existingKeys = new Set();
    existingSnap.forEach((d) => {
      existingKeys.add(d.id);
    });

    const nowISO = new Date().toISOString();
    let skippedCount = 0;
    const codesToInsert = [];

    for (const code of uniqueCodes) {
      const docId = `${platform}_${code}`;
      if (existingKeys.has(docId)) {
        skippedCount++;
      } else {
        codesToInsert.push(code);
      }
    }

    if (codesToInsert.length === 0) {
      const msg = `All ${uniqueCodes.length} code(s) already exist in inventory for ${platform.toUpperCase()}. No new codes added.`;
      showToast?.(msg, "info");
      showStatus(msg, "info");
      fileInput.value = "";
      restoreUploadBtn();
      await fetchPromoInventoryStock();
      return;
    }

    showStatus(`Storing ${codesToInsert.length} new codes (${skippedCount} existing skipped)...`, "info");

    // Batch commit in chunks of 400
    const BATCH_SIZE = 400;
    for (let i = 0; i < codesToInsert.length; i += BATCH_SIZE) {
      const chunk = codesToInsert.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);

      for (const code of chunk) {
        const docId = `${platform}_${code}`;
        const ref = doc(db, "promo_code_inventory", docId);
        batch.set(ref, {
          code: code,
          platform: platform,
          status: "available",
          campaign: campaign,
          created_at: nowISO,
          claimed_at: null,
          claimed_by_uid: null,
          claimed_by_family_id: null,
          claim_token: null
        });
      }

      await batch.commit();
    }

    const successMsg = `Successfully imported ${codesToInsert.length} ${platform.toUpperCase()} promo codes! (${skippedCount} already in inventory)`;
    showToast?.(successMsg, "success");
    showStatus(successMsg, "success");
    fileInput.value = "";
    await fetchPromoInventoryStock();
  } catch (err) {
    console.error("❌ Failed to upload promo codes:", err);
    showToast?.(`Failed to upload promo codes: ${err.message}`, "error");
    showStatus(`Error uploading codes: ${err.message}`, "error");
  } finally {
    restoreUploadBtn();
  }
}

/**
 * Fetches waitlist leads from Firestore with 5-minute caching and count aggregation
 */
export async function fetchLeadsList(force = false) {
  if (!firestoreCtx) return;
  const { db, collection, getDocs, getCountFromServer, query, where, showToast } = firestoreCtx;

  const now = Date.now();
  if (!force && lastLeadsFetchTime > 0 && now - lastLeadsFetchTime < CACHE_TTL_MS && leadsList.length > 0) {
    renderLeadsTable();
    return; // Re-use warm in-memory cache
  }

  if (isFetchingLeads) return;
  isFetchingLeads = true;

  try {
    const leadsCol = collection(db, "waitlist_leads");

    // 1. Efficient server-side aggregation for top KPI cards (0 document reads!)
    if (typeof getCountFromServer === "function") {
      try {
        const rescueQ = typeof where === "function" ? query(leadsCol, where("source", "==", "mealime_lifeboat_recipe_rescue")) : null;
        const claimedQ = typeof where === "function" ? query(leadsCol, where("status", "==", "claimed")) : null;

        const [totalSnap, rescueSnap, claimedSnap] = await Promise.all([
          getCountFromServer(leadsCol),
          rescueQ ? getCountFromServer(rescueQ) : Promise.resolve(null),
          claimedQ ? getCountFromServer(claimedQ) : Promise.resolve(null)
        ]);

        totalLeadsCount = totalSnap?.data?.()?.count ?? 0;
        if (rescueSnap?.data?.()) {
          totalRescueLeadsCount = rescueSnap.data().count ?? 0;
        }
        if (claimedSnap?.data?.()) {
          totalClaimedLeadsCount = claimedSnap.data().count ?? 0;
        }
      } catch (cntErr) {
        console.warn("⚠️ getCountFromServer error on waitlist_leads:", cntErr.message);
      }
    }

    // 2. Fetch every lead. Pagination, search and the filters all run in memory over leadsList, so
    // loading only the newest page left the rest unreachable (always "Page 1 of 1"). No orderBy
    // either: it would drop leads without created_at; they're sorted in memory below.
    const snap = await getDocs(leadsCol);

    leadsList = snap.docs.map((d) => ({
      id: d.id,
      ...d.data()
    }));

    // In-memory sort by created_at descending
    leadsList.sort((a, b) => {
      const timeA = a.created_at?.toMillis ? a.created_at.toMillis() : (a.created_at ? new Date(a.created_at).getTime() : 0);
      const timeB = b.created_at?.toMillis ? b.created_at.toMillis() : (b.created_at ? new Date(b.created_at).getTime() : 0);
      return timeB - timeA;
    });

    if (totalLeadsCount === 0) {
      totalLeadsCount = leadsList.length;
      totalRescueLeadsCount = leadsList.filter((l) => (l.source && l.source.includes("recipe_rescue")) || Number(l.recipe_count) > 0).length;
      totalClaimedLeadsCount = leadsList.filter(isLeadClaimed).length;
    }

    lastLeadsFetchTime = Date.now();
    currentLeadsPage = 1;

    // Update nav badge count
    setNavBadge("leads", totalLeadsCount);

    renderLeadsTable();
  } catch (err) {
    console.error("❌ Failed to fetch waitlist leads:", err);
    if (showToast) showToast(`Failed to load waitlist leads: ${err.message}`, "error");

    const tbody = document.getElementById("leadsTableBody");
    if (tbody) {
      tbody.innerHTML = `
        <tr><td colspan="6" class="px-6 py-12 text-center text-sm text-red-600 dark:text-red-400">Could not load the waitlist leads: ${escapeHTML(err.message)}</td></tr>
      `;
    }
  } finally {
    isFetchingLeads = false;
  }
}

/**
 * Returns filtered list of leads based on active search and dropdown states
 */
export function getFilteredLeads() {
  return leadsList.filter((lead) => {
    // 1. Source filter
    if (activeSourceFilter !== "ALL") {
      const src = lead.source || "";
      if (src !== activeSourceFilter) return false;
    }

    // 2. Recipe count filter
    const rCount = Number(lead.recipe_count) || (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0);
    if (activeRecipeFilter === "WITH_RECIPES" && rCount === 0) return false;
    if (activeRecipeFilter === "NO_RECIPES" && rCount > 0) return false;

    // 3. Claim status filter
    if (activeClaimFilter !== "ALL") {
      const claimed = isLeadClaimed(lead);
      if (activeClaimFilter === "CLAIMED" && !claimed) return false;
      if (activeClaimFilter === "UNCLAIMED" && claimed) return false;
    }

    // 4. Phone platform filter (answer to the welcome email's phone question)
    if (activePhoneFilter === "UNANSWERED" && lead.phone_platform) return false;
    if ((activePhoneFilter === "ios" || activePhoneFilter === "android") && lead.phone_platform !== activePhoneFilter) return false;

    // 5. Newsletter opt-in (#1611): with Copy emails or Export CSV, the list to send to
    if (activeNewsletterFilter === "OPTED_IN" && !isNewsletterOptIn(lead)) return false;
    if (activeNewsletterFilter === "NOT_OPTED_IN" && isNewsletterOptIn(lead)) return false;

    // 6. The 50% offer (#1960)
    if (!matchesOfferFilter(lead, activeOfferFilter)) return false;

    // 7. Search query
    if (activeSearchTerm) {
      const email = (lead.email || "").toLowerCase();
      const source = (lead.source || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      const promo = (lead.promo_code || "").toLowerCase();
      const family = (lead.claimed_by_family_id || "").toLowerCase();
      const phone = (lead.phone_platform || "").toLowerCase();
      const offerState = (lead.offer_status?.state || "").toLowerCase();
      if (
        !offerState.includes(activeSearchTerm) &&
        !email.includes(activeSearchTerm) &&
        !phone.includes(activeSearchTerm) &&
        !source.includes(activeSearchTerm) &&
        !status.includes(activeSearchTerm) &&
        !promo.includes(activeSearchTerm) &&
        !family.includes(activeSearchTerm)
      ) {
        return false;
      }
    }

    return true;
  });
}



/** Where a lead keeps an email Resend reports on (EMAIL_SLOTS in functions/resend_webhook_service.js). */
const LEAD_EMAIL_SLOTS = ["welcome_email", "welcome_email_last_manual_send", "launch_promo_email"];

/**
 * The email row's counts (#1449), from the Resend events the webhook writes onto each lead
 * (#1011): how many emails were delivered, opened, clicked, and bounced or rejected (Resend's
 * `bounced`, `failed` or `suppressed`). Each email counts once per event however often it repeated,
 * and a Resend id held by two records (a first welcome sent from the CRM) counts once. Counted over
 * leads already loaded for the table, so it costs no Firestore read. A record holds only its latest
 * send, so an email sent again replaces the earlier one's events.
 */
export function getLeadEmailMetrics(leads) {
  const metrics = { delivered: 0, opened: 0, clicked: 0, bouncedOrRejected: 0 };
  const seen = new Set();
  for (const lead of leads || []) {
    for (const slot of LEAD_EMAIL_SLOTS) {
      const record = lead?.[slot];
      const events = record?.events;
      if (!events) continue;
      const id = record.resend_id;
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      if (events.delivered) metrics.delivered += 1;
      if (events.opened) metrics.opened += 1;
      if (events.clicked) metrics.clicked += 1;
      if (events.bounced || events.failed || events.suppressed) metrics.bouncedOrRejected += 1;
    }
  }
  return metrics;
}

/**
 * Renders summary KPI stats and the leads data table
 */
export function renderLeadsTable() {
  // Update KPI counters across full dataset using server aggregations (0 document reads)
  const statTotal = document.getElementById("statLeadsTotal");
  const statRescue = document.getElementById("statLeadsRescue");
  const statClaimed = document.getElementById("statLeadsClaimed");
  const statPromoSent = document.getElementById("statLeadsPromoSent");

  const displayTotal = totalLeadsCount || leadsList.length;
  const displayRescue = totalRescueLeadsCount;
  const displayClaimed = totalClaimedLeadsCount;

  if (statTotal) statTotal.textContent = String(displayTotal);
  if (statRescue) statRescue.textContent = String(displayRescue);
  if (statClaimed) statClaimed.textContent = String(displayClaimed);
  // Counted over the loaded leads, which are all of them (fetchLeadsList loads every lead)
  if (statPromoSent) statPromoSent.textContent = String(leadsList.filter((lead) => lead.launch_promo_email?.status === "sent").length);

  // The email row (#1449): from the Resend events already on the loaded leads, so no extra reads
  const emailMetrics = getLeadEmailMetrics(leadsList);
  const emailStats = {
    statEmailsDelivered: emailMetrics.delivered,
    statEmailsOpened: emailMetrics.opened,
    statEmailsClicked: emailMetrics.clicked,
    statEmailsBounced: emailMetrics.bouncedOrRejected
  };
  for (const [id, value] of Object.entries(emailStats)) {
    const el = document.getElementById(id);
    if (el) el.textContent = String(value);
  }

  // The 50% offer row (#1960): from each lead's offer_status
  const offerCounts = countOfferStatuses(leadsList);
  const offerStats = {
    statOfferOn: offerCounts.on_offer,
    statOfferFullPriceTrials: offerCounts.full_price_trials,
    statOfferNotClaimed: offerCounts.not_claimed,
    statOfferNotChecked: offerCounts.not_checked
  };
  for (const [id, value] of Object.entries(offerStats)) {
    const el = document.getElementById(id);
    if (el) el.textContent = String(value);
  }

  // Render Table & Pagination
  const tbody = document.getElementById("leadsTableBody");
  const paginationBar = document.getElementById("leadsPaginationBar");
  const paginationInfo = document.getElementById("leadsPaginationInfo");
  const currentPageBadge = document.getElementById("leadsCurrentPageBadge");
  const prevPageBtn = document.getElementById("prevLeadsPageBtn");
  const nextPageBtn = document.getElementById("nextLeadsPageBtn");

  if (!tbody) return;

  const filtered = getFilteredLeads();
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / LEADS_PAGE_SIZE));

  if (currentLeadsPage > totalPages) {
    currentLeadsPage = totalPages;
  }

  // Update pagination bar controls
  const startIndex = (currentLeadsPage - 1) * LEADS_PAGE_SIZE;
  const endIndex = Math.min(startIndex + LEADS_PAGE_SIZE, totalFiltered);
  const pageItems = filtered.slice(startIndex, endIndex);

  if (paginationBar) {
    paginationBar.style.display = totalFiltered > 0 ? "flex" : "none";
  }
  if (paginationInfo) {
    paginationInfo.textContent = totalFiltered > 0
      ? `Showing ${startIndex + 1}–${endIndex} of ${totalFiltered} leads${totalLeadsCount > totalFiltered ? ` (${totalLeadsCount} total in database)` : ""}`
      : "Showing 0 leads";
  }
  if (currentPageBadge) {
    currentPageBadge.textContent = `Page ${currentLeadsPage} of ${totalPages}`;
  }
  if (prevPageBtn) prevPageBtn.disabled = currentLeadsPage <= 1;
  if (nextPageBtn) nextPageBtn.disabled = currentLeadsPage >= totalPages;

  if (filtered.length === 0) {
    tbody.innerHTML = `
      <tr><td colspan="6" class="px-6 py-12 text-center">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">No waitlist leads match</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Change the search or the filters.</p>
      </td></tr>
    `;
    return;
  }

  tbody.innerHTML = pageItems.map((lead) => {
    const rCount = Number(lead.recipe_count) || (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0);
    const dateStr = formatLeadDate(lead.created_at);
    const email = lead.email || "—";
    const recipesBadge = rCount > 0
      ? `<span title="${rCount} rescued recipe${rCount === 1 ? "" : "s"}" class="${RECIPES_BADGE}">${rCount} recipe${rCount === 1 ? "" : "s"}</span>`
      : "";

    // Nearly every lead has the standard offer, so only a different one is shown
    const offerHTML = lead.discount_percent !== undefined && Number(lead.discount_percent) !== STANDARD_DISCOUNT_PERCENT
      ? `<span title="Offer: ${escapeHTML(String(lead.discount_percent))}% off${lead.discount_price_usd !== undefined ? ` ($${escapeHTML(String(lead.discount_price_usd))}/yr)` : ""}" class="${BADGE.yellow} whitespace-nowrap">${escapeHTML(String(lead.discount_percent))}% off</span>`
      : "";
    const phoneHTML = PHONE_PLATFORMS[lead.phone_platform] ? getLeadPhoneBadgeHTML(lead) : "";
    const newsletterHTML = isNewsletterOptIn(lead)
      ? `<span title="Newsletter: opted in ${escapeHTML(formatLeadDate(lead.newsletter_opt_in_at))}" class="${BADGE.paprika} whitespace-nowrap">Newsletter</span>`
      : "";

    return `
      <tr class="lead-row cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5" data-lead-id="${escapeHTML(lead.id)}">
        <td class="py-3 pr-3 pl-4 sm:pl-6">
          <div class="flex items-center gap-3">
            ${phoneHTML}
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium break-all text-gray-900 dark:text-white">${escapeHTML(email)}</span>
                <button type="button" ${copyAttributes(email, "Copied the email.")} title="Copy the email" aria-label="Copy the email" class="flex-none rounded-xs p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">${solidIcon("clipboard-document", "size-4")}</button>
              </div>
              <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Joined ${escapeHTML(dateStr)}</div>
            </div>
          </div>
        </td>
        <td class="px-3 py-3">${getLeadSignupWeekdayHTML(lead)}</td>
        <td class="px-3 py-3"><div class="flex flex-wrap items-center gap-1.5">${getSourceBadgeHTML(lead.source)}${recipesBadge}${offerHTML}${newsletterHTML}</div></td>
        <td class="px-3 py-3"><div class="flex flex-col items-start gap-2">${getLeadClaimStatusBadgeHTML(lead)}${lead.offer_status || wasHandedOffer(lead) ? offerStatusCellHTML(lead.offer_status) : ""}</div></td>
        <td class="px-3 py-3">${getLeadEmailsBadgesHTML(lead)}</td>
        <td class="py-3 pr-4 pl-3 sm:pr-6">
          <div class="flex items-center justify-end gap-1.5">
            <button type="button" data-lead-id="${escapeHTML(lead.id)}" data-claim-url="${escapeHTML(getLeadDestinationUrl(lead))}" title="${lead.dynamic_link ? "ChottuLink saved. Copy the destination link" : "Copy the destination link to paste into ChottuLink"}" aria-label="Copy the destination link" class="copy-claim-btn ${BUTTON.small}">${solidIcon("link", "size-4 text-gray-400")}${lead.dynamic_link ? solidIcon("check", "size-4 text-green-600 dark:text-green-400") : ""}</button>
            <button type="button" data-lead-id="${escapeHTML(lead.id)}" title="Open the lead" aria-label="Open the lead" class="inspect-lead-btn ${BUTTON.small}">${solidIcon("chevron-right", "size-4 text-gray-400")}</button>
          </div>
        </td>
      </tr>
    `;
  }).join("");

  // Attach row and button click events to open lead detail modal
  const rows = tbody.querySelectorAll(".lead-row");
  rows.forEach((row) => {
    row.addEventListener("click", () => {
      const leadId = row.getAttribute("data-lead-id");
      if (leadId) openLeadModal(leadId);
    });
  });

  const inspectBtns = tbody.querySelectorAll(".inspect-lead-btn");
  inspectBtns.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const leadId = btn.getAttribute("data-lead-id");
      if (leadId) openLeadModal(leadId);
    });
  });

  const copyClaimBtns = tbody.querySelectorAll(".copy-claim-btn");
  copyClaimBtns.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const url = btn.getAttribute("data-claim-url");
      if (url) {
        navigator.clipboard.writeText(url).then(() => {
          firestoreCtx?.showToast?.("Copied destination URL! Ready to paste into ChottuLink.", "success");
        }).catch(() => {
          firestoreCtx?.showToast?.("Could not copy URL.", "error");
        });
      } else {
        firestoreCtx?.showToast?.("No claim token assigned yet. Click Inspect to generate one.", "warning");
      }
    });
  });
}

// A purple badge for the rescued-recipe count (BADGE has no purple).
const RECIPES_BADGE = "inline-flex items-center gap-x-1.5 rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 inset-ring inset-ring-purple-700/10 dark:bg-purple-400/10 dark:text-purple-400 dark:inset-ring-purple-400/30";

/**
 * Whether the lead ticked the newsletter box (#1605). lifeboat_service records it once and never
 * takes it back; there is no unsubscribe yet.
 */
export function isNewsletterOptIn(lead) {
  return lead?.newsletter_opt_in === true;
}

const NEWSLETTER_FORMS = {
  mealime_lifeboat_recipe_rescue: "the Recipe Rescue form",
  mealime_lifeboat_waitlist: "the Lifeboat waitlist form"
};

/** The drawer's Newsletter card: when and where they opted in, and the wording they agreed to. */
export function getLeadNewsletterHTML(lead) {
  if (!isNewsletterOptIn(lead)) {
    return `<span class="text-sm text-gray-500 dark:text-gray-400">Not opted in</span>`;
  }
  const source = lead.newsletter_opt_in_source || "";
  const form = NEWSLETTER_FORMS[source] || (source ? escapeHTML(source) : "an unknown form");
  const wording = lead.newsletter_consent_text
    ? `<p class="mt-2 text-xs text-gray-500 dark:text-gray-400">They agreed to: <q class="text-gray-700 italic dark:text-gray-300">${escapeHTML(lead.newsletter_consent_text)}</q></p>`
    : "";
  return `
    <div class="flex flex-wrap items-center gap-2">
      <span class="${BADGE.paprika}">Opted in</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">${escapeHTML(formatLeadDate(lead.newsletter_opt_in_at))}</span>
      <span class="text-sm text-gray-500 dark:text-gray-400">on ${form}</span>
    </div>${wording}`;
}

/**
 * Returns clean styled badge for campaign source
 */
function getSourceBadgeHTML(source) {
  if (!source) return `<span class="${BADGE.gray}">Direct</span>`;
  if (source.includes("recipe_rescue")) return `<span class="${BADGE.green}">Recipe Rescue</span>`;
  if (source.includes("waitlist")) return `<span class="${BADGE.blue}">Lifeboat waitlist</span>`;
  if (source.includes("coming_soon")) return `<span class="${BADGE.yellow}">Coming soon</span>`;
  return `<span class="${BADGE.gray}">${escapeHTML(source)}</span>`;
}

/**
 * Resets the claim on a lead/**
 * Resets the claim on a lead, detaching any assigned promo code and generating a fresh claim token
 * (Promo codes are intentionally NOT returned to the inventory pool to prevent collision if redeemed in App Store / Google Play).
 */
export async function resetLeadClaim(lead) {
  if (!lead) return;
  const leadId = lead.id;
  const email = lead.email || "this lead";

  const promoInfo = lead.promo_code
    ? `\n• Detach promo code "${lead.promo_code}" (${(lead.promo_platform || "").toUpperCase()}) without returning it to inventory`
    : "";

  const confirmed = window.confirm(
    `Are you sure you want to reset the claim for ${email}?${promoInfo}\n• Clear claimed family household and timestamps\n• Generate a fresh claim token & destination URL\n\nThis will allow the lead to claim their recipes and receive a new promo code on their desired platform.`
  );

  if (!confirmed) return;

  const resetBtn = document.getElementById("modalResetClaimBtn");
  if (resetBtn) {
    resetBtn.disabled = true;
    resetBtn.textContent = "Resetting…";
  }

  try {
    const { db, doc, setDoc, showToast } = firestoreCtx;
    if (!db || !doc || !setDoc) {
      throw new Error("Firestore context not available for resetting claim.");
    }

    // 1. Generate a new fresh claim token & destination claim URL
    const newToken = generateClientClaimToken();
    const count = Number(lead.recipe_count) || (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0);
    const newClaimUrl = `https://weeknightbiteapp.com/claim?token=${newToken}&count=${count}`;

    // 2. Update the lead document in Firestore (detaching promo code and clearing claim state)
    const leadUpdates = {
      status: "active",
      claimed_by_family_id: null,
      claimed_by_uid: null,
      claimed_at: null,
      claimed_count: 0,
      promo_code: null,
      promo_platform: null,
      promo_claimed_at: null,
      promo_status: "none",
      claim_token: newToken,
      claim_url: newClaimUrl,
      claim_reset_at: new Date().toISOString(),
      token_generated_at: new Date().toISOString()
    };

    await setDoc(doc(db, "waitlist_leads", leadId), leadUpdates, { merge: true });

    // 3. Update local in-memory lead model
    Object.assign(lead, leadUpdates);

    // 4. Refresh UI components
    renderLeadsTable();
    openLeadModal(leadId);
    fetchPromoInventoryStock();

    showToast?.("Claim successfully reset! Lead detached and ready for new claim. 🔄", "success");
  } catch (err) {
    console.error("❌ Failed to reset lead claim:", err);
    firestoreCtx?.showToast?.(`Failed to reset claim: ${err.message}`, "error");
    if (resetBtn) {
      resetBtn.disabled = false;
      resetBtn.innerHTML = RESET_CLAIM_LABEL;
    }
  }
}

// The launch offer most leads have; the leads table flags only the others
const STANDARD_DISCOUNT_PERCENT = 50;

const PHONE_PLATFORMS = {
  ios: { label: "iPhone", icon: platformLogo("ios") },
  android: { label: "Android", icon: platformLogo("android") }
};

/**
 * Returns the Joined on cell for the leads table: the weekday a lead signed up, with the time of
 * day under it. Both are in the viewer's time zone, like every date here, so an email sent at that
 * weekday and time on the viewer's own clock lands at the hour of the week the lead signed up in.
 */
export function getLeadSignupWeekdayHTML(lead) {
  const joined = parseLeadDate(lead.created_at);
  if (!joined) {
    return `<span class="text-sm text-gray-400 dark:text-gray-500" title="No signup date">—</span>`;
  }
  const weekday = joined.toLocaleDateString("en-US", { weekday: "long" });
  const time = joined.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `
    <div title="Joined ${escapeHTML(formatLeadDate(lead.created_at))}, in your time zone" class="whitespace-nowrap">
      <div class="text-sm font-medium text-gray-900 dark:text-white">${weekday}</div>
      <div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">${escapeHTML(time)}</div>
    </div>`;
}

/**
 * Returns the Phone cell for the leads table: an Apple or Android icon for the answer to the welcome
 * email's phone question, with the answer time on hover
 */
export function getLeadPhoneBadgeHTML(lead) {
  const platform = PHONE_PLATFORMS[lead.phone_platform];
  if (!platform) {
    return `<span class="text-sm text-gray-400 dark:text-gray-500" title="Hasn't answered the welcome email's phone question">—</span>`;
  }
  const tooltip = `${platform.label} · answered ${formatLeadDate(lead.phone_platform_answered_at)}`;
  return `<span role="img" aria-label="${platform.label}" title="${escapeHTML(tooltip)}" class="inline-flex size-8 flex-none items-center justify-center rounded-md bg-gray-50 ring-1 ring-gray-900/5 ring-inset dark:bg-white/5 dark:ring-white/10">${platform.icon}</span>`;
}

/**
 * What Resend reported about an email (#1011), as the pill's label: the furthest the email got,
 * or what stopped it. Null when no event has arrived, so the pill keeps saying "Sent".
 */
export function getEmailDeliveryStatus(record) {
  const events = record?.events;
  if (!events) return null;
  if (events.complained) return { label: "Spam", tone: "red" };
  if (events.bounced) return { label: "Bounced", tone: "red" };
  if (events.failed || events.suppressed) return { label: "Failed", tone: "red" };
  if (events.clicked) return { label: "Clicked", tone: "green" };
  if (events.opened) return { label: "Opened", tone: "green" };
  if (events.delivered) return { label: "Delivered", tone: "green" };
  if (events.delayed) return { label: "Delayed", tone: "yellow" };
  return null;
}

const EMAIL_EVENT_LABELS = {
  delivered: "Delivered", delayed: "Delayed", opened: "Opened", clicked: "Clicked",
  bounced: "Bounced", complained: "Marked as spam", failed: "Failed", suppressed: "Suppressed"
};

/**
 * One line with the email's events in the order they happen, each with its first time and how
 * often it repeated: "Resend: Delivered Oct 6, 11:30 · Opened Oct 6, 11:45 (2×)". Empty when
 * nothing has arrived.
 */
export function getEmailEventsHTML(record) {
  const events = record?.events;
  if (!events) return "";
  const parts = Object.keys(EMAIL_EVENT_LABELS)
    .filter((key) => events[key])
    .map((key) => {
      const entry = events[key];
      const times = entry.count > 1 ? ` (${entry.count}×)` : "";
      const why = key === "bounced" && entry.message ? `: ${escapeHTML(entry.message)}` : "";
      return `${EMAIL_EVENT_LABELS[key]} ${formatLeadDate(entry.at)}${times}${why}`;
    });
  return parts.length ? `Resend: ${parts.join(" · ")}` : "";
}

/** True when a bounce or a complaint has put the lead's address off every send (#1011). */
export function isLeadEmailSuppressed(lead) {
  return typeof lead?.email_suppressed_at === "string" && lead.email_suppressed_at !== "";
}

/** The red line both email cards show for a suppressed address, or empty. */
export function getSuppressionNoteHTML(lead) {
  const lines = [];
  if (isLeadEmailSuppressed(lead)) {
    const what = lead.email_suppressed_reason === "complained" ? "marked an email as spam" : "bounced";
    lines.push(`<span class="font-semibold text-red-700 dark:text-red-400">No more email to this address</span>: it ${what} ${formatLeadDate(lead.email_suppressed_at)}.`);
  }
  if (isLeadUnsubscribed(lead)) {
    lines.push(`<span class="font-semibold text-red-700 dark:text-red-400">No more email to this lead</span>: they unsubscribed ${formatLeadDate(lead.unsubscribed_at)}.`);
  }
  return lines.join("<br>");
}

/** True when the lead unsubscribed, from the emails' Unsubscribe link or a mail client's one-click button (#2308). */
export function isLeadUnsubscribed(lead) {
  return typeof lead?.unsubscribed_at === "string" && lead.unsubscribed_at !== "";
}

/** Why the drawer's send buttons are off, or empty when the lead may be emailed. The functions refuse these too. */
export function getLeadEmailBlockReason(lead) {
  if (isLeadEmailSuppressed(lead)) return "This address bounced or marked an email as spam";
  if (isLeadUnsubscribed(lead)) return "This lead unsubscribed";
  return "";
}

/**
 * What tells us the lead has a WeeknightBite account, whatever address it uses (#2308): a claim, the claim link opened
 * signed in (`claim_link_opened_uids`; on Android also `android_offer_uids`), or the lead's own word on the stop page
 * (`has_account_reported_at`, from "Already using WeeknightBite?"). Any of them keeps the lead out of retargeting.
 */
export function getLeadAccountEvidence(lead) {
  const evidence = [];
  if (lead?.claimed_by_uid) evidence.push("claimed the recipes signed in");
  const opened = new Set([
    ...(Array.isArray(lead?.claim_link_opened_uids) ? lead.claim_link_opened_uids : []),
    ...(Array.isArray(lead?.android_offer_uids) ? lead.android_offer_uids : [])
  ]);
  if (opened.size) evidence.push(opened.size === 1 ? "opened the claim link signed in" : `opened the claim link signed in to ${opened.size} accounts`);
  if (lead?.has_account_reported_at) evidence.push("said they already use WeeknightBite");
  return evidence;
}

/**
 * Returns the welcome email status pill label and colors for a lead
 */
export function getWelcomeEmailStatus(lead) {
  const status = lead.welcome_email?.status;
  if (status === "sent") return getEmailDeliveryStatus(lead.welcome_email) || { label: "Sent", tone: "green" };
  if (status === "failed") return { label: "Failed", tone: "red" };
  if (status === "sending") return { label: "Sending", tone: "blue" };
  return { label: "Not sent", tone: "gray" };
}

/**
 * Returns the HTML lines under the welcome email card: send history and the phone answer
 */
export function getWelcomeEmailDetailsHTML(lead) {
  const lines = [];
  const suppression = getSuppressionNoteHTML(lead);
  if (suppression) lines.push(suppression);
  const welcome = lead.welcome_email;
  if (welcome?.status === "sent") {
    lines.push(`${welcome.manual ? "Sent from the CRM" : "Sent automatically"} ${formatLeadDate(welcome.sent_at)}`);
    const events = getEmailEventsHTML(welcome);
    if (events) lines.push(events);
  } else if (welcome?.status === "failed") {
    lines.push(`Automatic send failed ${formatLeadDate(welcome.failed_at)}: ${escapeHTML(welcome.error || "unknown error")}`);
  }
  const manual = lead.welcome_email_last_manual_send;
  if (manual && !(welcome?.manual && welcome.sent_at === manual.sent_at)) {
    lines.push(`Last sent from the CRM ${formatLeadDate(manual.sent_at)}`);
    const events = getEmailEventsHTML(manual);
    if (events) lines.push(events);
  }

  const platform = PHONE_PLATFORMS[lead.phone_platform];
  lines.push(platform
    ? `<span class="inline-flex items-center gap-1.5 align-middle">Phone: ${platform.icon}<strong class="font-semibold text-gray-900 dark:text-white">${platform.label}</strong> (answered ${formatLeadDate(lead.phone_platform_answered_at)})</span>`
    : "Phone: not answered yet");
  return lines.map((line) => `<div>${line}</div>`).join("");
}

/**
 * How many recipes the lead can claim, as the launch promo email counts them
 * (claimableRecipeCount in functions/waitlist_launch_promo_email.js)
 */
export function getLeadClaimableRecipeCount(lead) {
  const ready = lead.recipes_stats?.ready;
  if (typeof ready === "number") return ready;
  if (typeof lead.recipe_count === "number") return lead.recipe_count;
  return Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0;
}

/**
 * The link the launch promo email puts behind its claim button, as the function picks it
 * (resolveClaimLink in functions/waitlist_launch_promo_email.js): the ChottuLink unless it was
 * saved before the last claim reset, else the destination link. `why` explains a fallback.
 */
export function getLaunchPromoClaimLink(lead) {
  const chottu = typeof lead.dynamic_link === "string" ? lead.dynamic_link.trim() : "";
  const savedBeforeReset =
    lead.claim_reset_at && (!lead.chottulink_updated_at || lead.chottulink_updated_at < lead.claim_reset_at);
  if (/^https:\/\/\S+$/.test(chottu) && !savedBeforeReset) return { url: chottu, kind: "chottulink", why: "" };
  const why = !chottu
    ? "no ChottuLink saved"
    : savedBeforeReset
      ? "the ChottuLink was saved before the last claim reset"
      : "the saved ChottuLink isn't an https link";
  return { url: lead.claim_url || "", kind: "destination", why };
}

function describeLaunchPromoClaimLink(lead) {
  const link = getLaunchPromoClaimLink(lead);
  if (!link.url) return "no claim link, so it can't be sent";
  return link.kind === "chottulink" ? "the ChottuLink" : `the destination link (${link.why})`;
}

/**
 * Returns the launch promo email status pill label and colors for a lead
 */
export function getLaunchPromoEmailStatus(lead) {
  if (lead.launch_promo_email?.status === "sent") return getEmailDeliveryStatus(lead.launch_promo_email) || { label: "Sent", tone: "green" };
  // The automatic send (#1573) records a failure and never retries; the CRM button sends again.
  if (lead.launch_promo_email?.status === "failed") return { label: "Failed", tone: "red" };
  if (lead.launch_promo_email?.status === "sending") return { label: "Sending", tone: "yellow" };
  return { label: "Not sent", tone: "gray" };
}

/**
 * The leads table's Emails cell: a Welcome and a Promo pill, colored as in the lead modal, with
 * the send date on hover
 */
export function getLeadEmailsBadgesHTML(lead) {
  const welcome = getWelcomeEmailStatus(lead);
  const welcomeTitle = lead.welcome_email?.status === "sent"
    ? `Welcome email sent ${formatLeadDate(lead.welcome_email.sent_at)}`
    : lead.welcome_email?.status === "failed"
      ? `Welcome email failed ${formatLeadDate(lead.welcome_email.failed_at)}`
      : `Welcome email: ${welcome.label.toLowerCase()}`;
  const promo = getLaunchPromoEmailStatus(lead);
  const sends = lead.launch_promo_email?.send_count > 1 ? ` (${lead.launch_promo_email.send_count} sends)` : "";
  const promoTitle = lead.launch_promo_email?.status === "sent"
    ? `Launch promo sent ${formatLeadDate(lead.launch_promo_email.sent_at)}${sends}`
    : "Launch promo: not sent";
  const pill = (name, status, title) => `<span title="${escapeHTML(title)}" class="${BADGE[status.tone]} whitespace-nowrap">${name}: ${status.label}</span>`;
  return `<div class="flex flex-col items-start gap-1">${pill("Welcome", welcome, welcomeTitle)}${pill("Promo", promo, promoTitle)}</div>`;
}

/**
 * Returns the HTML lines under the launch promo card: the last send, and what the email will say
 */
export function getLaunchPromoDetailsHTML(lead) {
  const lines = [];
  const suppression = getSuppressionNoteHTML(lead);
  if (suppression) lines.push(suppression);
  const promo = lead.launch_promo_email;
  if (promo?.status === "failed") {
    lines.push(`The automatic send failed ${formatLeadDate(promo.failed_at)}: ${escapeHTML(promo.error || "unknown error")}. Send it again from here.`);
  }
  if (promo?.status === "sent") {
    const times = promo.send_count > 1 ? ` (${promo.send_count} sends)` : "";
    const by = promo.sent_by === "automatic" ? "automatically once a recipe was ready" : promo.founders_note ? "with a note from the founders" : "without a note";
    lines.push(`Last sent ${formatLeadDate(promo.sent_at)}${times}, ${by}`);
    const events = getEmailEventsHTML(promo);
    if (events) lines.push(events);
  }
  const count = getLeadClaimableRecipeCount(lead);
  const recipes = count === 0 ? "no recipes, so it offers the discount only" : count === 1 ? "1 recipe to claim" : `${count} recipes to claim`;
  lines.push(`The email will say: ${recipes}, ${escapeHTML(String(lead.discount_percent ?? "?"))}% off`);
  lines.push(`Claim button: ${describeLaunchPromoClaimLink(lead)}`);
  return lines.map((line) => `<div>${line}</div>`).join("");
}

/**
 * Opens the compose dialog for the launch promo email: an optional note from the founders, then
 * Send. Resolves with the note ("" for none), or null when cancelled.
 */
export function promptLaunchPromoNote(lead) {
  const modal = document.getElementById("launchPromoComposeModal");
  const summary = document.getElementById("launchPromoComposeSummary");
  const textarea = document.getElementById("launchPromoFoundersNote");
  const counter = document.getElementById("launchPromoFoundersNoteCount");
  const sendBtn = document.getElementById("launchPromoComposeSendBtn");
  const cancelBtn = document.getElementById("launchPromoComposeCancelBtn");
  if (!modal || !textarea || !sendBtn || !cancelBtn) return Promise.resolve(null);

  const email = escapeHTML(lead.email || "this lead");
  const again = lead.launch_promo_email?.status === "sent" ? " They already received it; this sends it again." : "";
  if (summary) summary.innerHTML = `To <strong class="font-semibold text-gray-900 dark:text-white">${email}</strong>, claim button: ${describeLaunchPromoClaimLink(lead)}.${again}`;
  textarea.value = "";
  const updateCount = () => {
    if (counter) counter.textContent = `${textarea.value.length} / ${textarea.maxLength}`;
  };
  updateCount();
  showDialog("launchPromoComposeModal");
  textarea.focus();

  // Send and Cancel close the dialog; so do Esc and the backdrop, which count as Cancel.
  return new Promise((resolve) => {
    let result = null;
    textarea.oninput = updateCount;
    sendBtn.onclick = () => {
      result = textarea.value.trim();
      hideDialog("launchPromoComposeModal");
    };
    cancelBtn.onclick = () => hideDialog("launchPromoComposeModal");
    modal.addEventListener("close", () => {
      textarea.oninput = null;
      sendBtn.onclick = null;
      cancelBtn.onclick = null;
      resolve(result);
    }, { once: true });
  });
}

/**
 * Sends the launch promo email to one lead via the sendWaitlistLaunchPromoEmail callable, after
 * the compose dialog. An empty note sends the email without one.
 */
export async function sendLaunchPromoEmailToLead(lead) {
  if (!lead) return;
  const foundersNote = await promptLaunchPromoNote(lead);
  if (foundersNote === null) return;
  const email = lead.email || "this lead";

  const btn = document.getElementById("modalSendLaunchPromoBtn");
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Sending…";
  }

  try {
    const { functions, httpsCallable, showToast } = firestoreCtx;
    if (!functions || !httpsCallable) {
      throw new Error("Cloud Functions context not available.");
    }
    const sendFn = httpsCallable(functions, "sendWaitlistLaunchPromoEmail");
    const { data } = await sendFn({ leadId: lead.id, foundersNote });

    if (data.launch_promo_email) lead.launch_promo_email = data.launch_promo_email;

    renderLeadsTable();
    openLeadModal(lead.id);
    showToast?.(`Launch promo email sent to ${email} 🎉`, "success");
  } catch (err) {
    console.error("❌ Failed to send launch promo email:", err);
    firestoreCtx?.showToast?.(`Failed to send launch promo email: ${err.message}`, "error");
    if (btn) {
      btn.disabled = false;
      btn.textContent = "Send the launch promo";
    }
  }
}

/**
 * Sends the welcome email to one lead via the sendWaitlistWelcomeEmail callable, the only way
 * it is sent since #1578.
 */
export async function sendWelcomeEmailToLead(lead) {
  if (!lead) return;
  const email = lead.email || "this lead";
  const already = lead.welcome_email?.status === "sent" ? "\n\nThey already received it; this sends it again." : "";
  if (!window.confirm(`Send the welcome email to ${email} now?${already}`)) return;

  const btn = document.getElementById("modalSendWelcomeBtn");
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Sending…";
  }

  try {
    const { functions, httpsCallable, showToast } = firestoreCtx;
    if (!functions || !httpsCallable) {
      throw new Error("Cloud Functions context not available.");
    }
    const sendFn = httpsCallable(functions, "sendWaitlistWelcomeEmail");
    const { data } = await sendFn({ leadId: lead.id });

    if (data.welcome_email) lead.welcome_email = data.welcome_email;
    if (data.welcome_email_last_manual_send) lead.welcome_email_last_manual_send = data.welcome_email_last_manual_send;

    renderLeadsTable();
    openLeadModal(lead.id);
    showToast?.(`Welcome email sent to ${email} ✉️`, "success");
  } catch (err) {
    console.error("❌ Failed to send welcome email:", err);
    firestoreCtx?.showToast?.(`Failed to send welcome email: ${err.message}`, "error");
    if (btn) {
      btn.disabled = false;
      btn.textContent = "Send the welcome email";
    }
  }
}

/**
 * Opens the detail modal for a specific lead
 */
// The drawer's button labels, restored after a run.
const RESET_CLAIM_LABEL = `${solidIcon("arrow-path", "size-4")}Reset the claim`;
const GENERATE_TOKEN_LABEL = `${solidIcon("bolt", "size-4")}Generate a claim token`;

// One labelled value in the claim card's grid.
function claimDetailHTML(label, valueHTML) {
  return `
    <div class="rounded-md bg-white px-3 py-2 ring-1 ring-gray-900/5 ring-inset dark:bg-gray-900/40 dark:ring-white/10">
      <div class="text-xs font-medium text-gray-500 dark:text-gray-400">${label}</div>
      <div class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">${valueHTML}</div>
    </div>`;
}

export function openLeadModal(leadId) {
  const lead = leadsList.find((l) => l.id === leadId);
  if (!lead) return;

  selectedLeadItem = lead;
  const modal = document.getElementById("leadDetailModal");
  if (!modal) return;

  const email = lead.email || "—";
  const urls = Array.isArray(lead.recipe_urls) ? lead.recipe_urls : [];
  const rCount = Number(lead.recipe_count) || urls.length;
  const discountPercent = lead.discount_percent !== undefined ? lead.discount_percent : 50;
  const discountPrice = lead.discount_price_usd !== undefined ? `$${lead.discount_price_usd}` : "$29.99";
  const standardPrice = lead.standard_price_usd !== undefined ? `$${lead.standard_price_usd}` : "$59.99";

  // Elements
  const titleEl = document.getElementById("modalLeadTitle");
  const createdEl = document.getElementById("modalLeadCreated");
  const emailEl = document.getElementById("modalLeadEmail");
  const sourceEl = document.getElementById("modalLeadSource");
  const pricingEl = document.getElementById("modalLeadPricing");
  const lastActiveEl = document.getElementById("modalLeadLastActive");
  const newsletterEl = document.getElementById("modalLeadNewsletter");
  const recipeBadgeEl = document.getElementById("modalLeadRecipeBadge");
  const copyRecipeUrlsBtn = document.getElementById("modalCopyRecipeUrlsBtn");
  const recipesListEl = document.getElementById("modalLeadRecipesList");
  const rawJsonEl = document.getElementById("modalLeadRawJson");
  const toggleRawBtn = document.getElementById("modalToggleRawJsonBtn");
  const copyRawBtn = document.getElementById("modalCopyRawJsonBtn");

  if (titleEl) titleEl.textContent = email;
  if (createdEl) createdEl.textContent = `Joined ${formatLeadDate(lead.created_at)}`;
  if (emailEl) emailEl.textContent = email;
  if (sourceEl) sourceEl.innerHTML = getSourceBadgeHTML(lead.source);
  if (pricingEl) pricingEl.textContent = `${discountPercent}% off (${discountPrice}/yr); standard ${standardPrice}`;
  if (lastActiveEl) lastActiveEl.textContent = formatLeadDate(lead.last_active_at || lead.created_at);
  if (newsletterEl) newsletterEl.innerHTML = getLeadNewsletterHTML(lead);
  if (recipeBadgeEl) recipeBadgeEl.textContent = `${rCount} recipe${rCount === 1 ? "" : "s"}`;

  // Claim & Promo Details Card elements
  const claimStatusCard = document.getElementById("modalLeadClaimStatusCard");
  const claimStatusIcon = document.getElementById("modalClaimStatusIcon");
  const claimStatusPill = document.getElementById("modalClaimStatusPill");
  const claimedTimestamp = document.getElementById("modalClaimedTimestamp");
  const resetClaimBtn = document.getElementById("modalResetClaimBtn");
  const claimDetailsGrid = document.getElementById("modalClaimDetailsGrid");

  const isClaimed = isLeadClaimed(lead);
  const isFullyClaimed = lead.status === "claimed" || Boolean(lead.claimed_by_family_id) || Boolean(lead.claimed_at);
  const claimState = isFullyClaimed ? "claimed" : lead.promo_code ? "allocated" : "open";

  // data-state colours the card: open (grey), allocated (sky), claimed (green)
  if (claimStatusCard) claimStatusCard.dataset.state = claimState;
  if (claimStatusIcon) {
    claimStatusIcon.innerHTML = {
      claimed: solidIcon("check-circle", "size-5 text-green-600 dark:text-green-400"),
      allocated: solidIcon("ticket", "size-5 text-sky-600 dark:text-sky-400"),
      open: solidIcon("clock", "size-5 text-gray-400")
    }[claimState];
  }
  if (claimStatusPill) {
    claimStatusPill.className = { claimed: BADGE.green, allocated: BADGE.blue, open: BADGE.gray }[claimState];
    claimStatusPill.textContent = { claimed: "Claimed", allocated: "Promo allocated", open: "Unclaimed" }[claimState];
  }
  if (claimedTimestamp) {
    if (isClaimed) {
      const claimDate = formatLeadDate(lead.claimed_at || lead.promo_claimed_at);
      claimedTimestamp.textContent = isFullyClaimed ? `Claimed ${claimDate}` : `Allocated ${claimDate}`;
      claimedTimestamp.className = isFullyClaimed
        ? "text-sm font-semibold text-green-700 dark:text-green-400"
        : "text-sm font-semibold text-sky-700 dark:text-sky-400";
    } else {
      claimedTimestamp.textContent = "Waiting for a claim in the app";
      claimedTimestamp.className = "text-sm text-gray-500 dark:text-gray-400";
    }
  }

  const accountNote = document.getElementById("modalLeadAccountNote");
  if (accountNote) {
    const evidence = getLeadAccountEvidence(lead);
    accountNote.textContent = evidence.length
      ? `Has a WeeknightBite account: ${evidence.join(", ")}.`
      : "No account seen on this lead yet.";
  }

  const welcomePill = document.getElementById("modalWelcomeStatusPill");
  const welcomeDetails = document.getElementById("modalWelcomeDetails");
  const sendWelcomeBtn = document.getElementById("modalSendWelcomeBtn");
  if (welcomePill) {
    const { label, tone } = getWelcomeEmailStatus(lead);
    welcomePill.className = BADGE[tone];
    welcomePill.textContent = label;
  }
  if (welcomeDetails) welcomeDetails.innerHTML = getWelcomeEmailDetailsHTML(lead);
  if (sendWelcomeBtn) {
    // A bounced or complaining address (#1011), or a lead who unsubscribed (#2308), gets nothing more; the function
    // refuses it too.
    sendWelcomeBtn.disabled = Boolean(getLeadEmailBlockReason(lead));
    sendWelcomeBtn.title = getLeadEmailBlockReason(lead);
    sendWelcomeBtn.textContent = lead.welcome_email?.status === "sent" ? "Send again" : "Send the welcome email";
    sendWelcomeBtn.onclick = () => sendWelcomeEmailToLead(lead);
  }

  const launchPromoPill = document.getElementById("modalLaunchPromoStatusPill");
  const launchPromoDetails = document.getElementById("modalLaunchPromoDetails");
  const sendLaunchPromoBtn = document.getElementById("modalSendLaunchPromoBtn");
  if (launchPromoPill) {
    const { label, tone } = getLaunchPromoEmailStatus(lead);
    launchPromoPill.className = BADGE[tone];
    launchPromoPill.textContent = label;
  }
  if (launchPromoDetails) launchPromoDetails.innerHTML = getLaunchPromoDetailsHTML(lead);
  if (sendLaunchPromoBtn) {
    sendLaunchPromoBtn.disabled = Boolean(getLeadEmailBlockReason(lead));
    sendLaunchPromoBtn.title = getLeadEmailBlockReason(lead);
    sendLaunchPromoBtn.textContent = lead.launch_promo_email?.status === "sent" ? "Send again" : "Send the launch promo";
    sendLaunchPromoBtn.onclick = () => sendLaunchPromoEmailToLead(lead);
  }

  if (resetClaimBtn) {
    // Reset only applies to a lead that claimed or got a promo code
    if (isClaimed) {
      resetClaimBtn.style.display = "inline-flex";
      resetClaimBtn.disabled = false;
      resetClaimBtn.innerHTML = RESET_CLAIM_LABEL;
      resetClaimBtn.onclick = () => resetLeadClaim(lead);
    } else {
      resetClaimBtn.style.display = "none";
    }
  }

  if (claimDetailsGrid) {
    if (isClaimed) {
      const platform = (lead.promo_platform || "").toLowerCase();
      const platformLabel = platform === "ios" ? "iOS (App Store)" : (platform === "android" ? "Android (Google Play)" : (platform ? platform.toUpperCase() : "Mobile app"));

      let promoValue = `<span class="font-normal text-gray-500 dark:text-gray-400">None</span>`;
      if (lead.promo_code) {
        const code = escapeHTML(lead.promo_code);
        promoValue = `<span class="flex items-center justify-between gap-2"><code class="font-mono text-green-700 dark:text-green-400">${code}</code><button type="button" ${copyAttributes(lead.promo_code, "Copied the promo code.", "success")} class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Copy</button></span>`;
      } else if (lead.promo_status === "exhausted") {
        promoValue = `<span class="text-amber-700 dark:text-amber-400">Promo codes ran out</span>`;
      }

      claimDetailsGrid.innerHTML = [
        claimDetailHTML("Promo code", promoValue),
        claimDetailHTML("Claimed on", escapeHTML(platformLabel)),
        claimDetailHTML("Household", `<span class="font-mono text-xs break-all">${escapeHTML(lead.claimed_by_family_id || "—")}</span>`),
        claimDetailHTML("Recipes imported", `${lead.claimed_count || 0} recipe${lead.claimed_count === 1 ? "" : "s"}`)
      ].join("");
    } else {
      claimDetailsGrid.innerHTML = `
        <p class="text-sm text-gray-500 sm:col-span-2 dark:text-gray-400">
          Not claimed in the app yet. When they open the claim link, their recipes and a promo code are allocated and show here.
        </p>
      `;
    }
  }

  renderLeadOfferSection(lead);

  // Claim & ChottuLink Section elements
  const claimBadgeEl = document.getElementById("modalLeadClaimBadge");
  const claimUrlEl = document.getElementById("modalLeadClaimUrl");
  const generateClaimTokenBtn = document.getElementById("modalGenerateClaimTokenBtn");
  const copyClaimUrlBtn = document.getElementById("modalCopyClaimUrlBtn");
  const chottuInput = document.getElementById("modalLeadChottuInput");
  const chottuStatusText = document.getElementById("modalChottuStatusText");
  const saveChottuLinkBtn = document.getElementById("modalSaveChottuLinkBtn");
  const copyChottuLinkBtn = document.getElementById("modalCopyChottuLinkBtn");
  const mailtoLeadBtn = document.getElementById("modalMailtoLeadBtn");
  const copyEmailTemplateBtn = document.getElementById("modalCopyEmailTemplateBtn");
  const testClaimUrlBtn = document.getElementById("modalTestClaimUrlBtn");

  if (claimBadgeEl) {
    if (lead.recipes_stats?.ready !== undefined) {
      claimBadgeEl.textContent = `${lead.recipes_stats.ready} ready of ${lead.recipes_stats.total || rCount}`;
    } else {
      claimBadgeEl.textContent = `${rCount} rescued recipe${rCount === 1 ? "" : "s"}`;
    }
  }

  const updateClaimUrlDisplay = () => {
    const destUrl = getLeadDestinationUrl(lead);
    const activeClaimUrl = getLeadActiveClaimUrl(lead);

    if (!lead.claim_token) {
      if (generateClaimTokenBtn) {
        generateClaimTokenBtn.style.display = "inline-flex";
        generateClaimTokenBtn.disabled = false;
        generateClaimTokenBtn.innerHTML = GENERATE_TOKEN_LABEL;
      }
      if (claimUrlEl) {
        claimUrlEl.innerHTML = `<span class="font-sans font-medium text-amber-700 dark:text-amber-400">No claim token yet. Generate one first.</span>`;
      }
      if (testClaimUrlBtn) {
        testClaimUrlBtn.removeAttribute("href");
        testClaimUrlBtn.toggleAttribute("data-disabled", true);
      }
    } else {
      if (generateClaimTokenBtn) {
        generateClaimTokenBtn.style.display = "none";
      }
      if (claimUrlEl) {
        claimUrlEl.textContent = destUrl;
      }
      if (testClaimUrlBtn) {
        testClaimUrlBtn.href = activeClaimUrl;
        testClaimUrlBtn.toggleAttribute("data-disabled", false);
      }
    }
  };

  updateClaimUrlDisplay();

  if (generateClaimTokenBtn) {
    generateClaimTokenBtn.onclick = async () => {
      generateClaimTokenBtn.disabled = true;
      generateClaimTokenBtn.textContent = "Generating…";
      try {
        const { db, doc, setDoc, showToast } = firestoreCtx;
        if (!db || !doc || !setDoc) {
          throw new Error("Firestore context not available for updating lead.");
        }
        const newToken = generateClientClaimToken();
        const count = Number(lead.recipe_count) || (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0);
        const newClaimUrl = `https://weeknightbiteapp.com/claim?token=${newToken}&count=${count}`;

        await setDoc(
          doc(db, "waitlist_leads", lead.id),
          {
            claim_token: newToken,
            claim_url: newClaimUrl,
            recipes_stats: lead.recipes_stats || { total: count, ready: count, failed: 0 },
            token_generated_at: new Date().toISOString()
          },
          { merge: true }
        );

        lead.claim_token = newToken;
        lead.claim_url = newClaimUrl;
        if (!lead.recipes_stats) {
          lead.recipes_stats = { total: count, ready: count, failed: 0 };
        }

        updateClaimUrlDisplay();
        updateEmailDraft();
        renderLeadsTable();

        showToast?.("Generated claim token successfully! ⚡", "success");
      } catch (err) {
        console.error("Failed to generate claim token:", err);
        firestoreCtx?.showToast?.(`Failed to generate claim token: ${err.message}`, "error");
        generateClaimTokenBtn.disabled = false;
        generateClaimTokenBtn.innerHTML = GENERATE_TOKEN_LABEL;
      }
    };
  }

  if (copyClaimUrlBtn) {
    copyClaimUrlBtn.onclick = () => {
      const destUrl = getLeadDestinationUrl(lead);
      if (!destUrl) {
        firestoreCtx?.showToast?.("Please generate a claim token first!", "warning");
        return;
      }
      navigator.clipboard.writeText(destUrl).then(() => {
        firestoreCtx?.showToast?.("Copied destination URL to clipboard! Ready to paste into ChottuLink.", "success");
      });
    };
  }

  if (chottuInput) {
    chottuInput.value = lead.dynamic_link || "";
  }

  // data-saved turns the status green
  const showChottuSaved = (saved) => {
    if (!chottuStatusText) return;
    chottuStatusText.textContent = saved ? "Saved" : "None saved yet";
    chottuStatusText.toggleAttribute("data-saved", saved);
  };
  const hasChottu = Boolean(lead.dynamic_link);
  showChottuSaved(hasChottu);

  if (copyChottuLinkBtn) {
    copyChottuLinkBtn.style.display = hasChottu ? "inline-flex" : "none";
    copyChottuLinkBtn.onclick = () => {
      if (lead.dynamic_link) {
        navigator.clipboard.writeText(lead.dynamic_link).then(() => {
          firestoreCtx?.showToast?.("Copied ChottuLink to clipboard!", "success");
        });
      }
    };
  }

  const updateEmailDraft = () => {
    const destUrl = getLeadDestinationUrl(lead);
    const linkForEmail = lead.dynamic_link || destUrl || "[Claim Link]";
    const recipient = lead.email || "";
    const hasRescued = rCount > 0;

    const emailSubject = hasRescued
      ? "Your rescued recipes are waiting in WeeknightBite! 🍽️"
      : "Your WeeknightBite early access discount is ready! 🎟️";

    const emailBodyText = hasRescued
      ? `Hi there,

Great news! The recipes you saved during our waitlist are ready and waiting for you in WeeknightBite.

Click the link below on your phone to open the app and import your ${rCount} recipes directly into your family recipe catalog:

${linkForEmail}

(If you open this link on a computer, it will display a QR code you can scan with your phone's camera).

Tip: If you don't see your recipes immediately after downloading the app, simply tap this link one more time from your phone.

Happy cooking!
The WeeknightBite Team`
      : `Hi there,

Great news! WeeknightBite is officially live, and your special early access discount is waiting for you.

Click the link below on your phone to open the app and automatically activate your discount:

${linkForEmail}

(If you open this link on a computer, it will display a QR code you can scan with your phone's camera).

Happy cooking!
The WeeknightBite Team`;

    if (mailtoLeadBtn) {
      mailtoLeadBtn.href = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBodyText)}`;
    }

    if (copyEmailTemplateBtn) {
      copyEmailTemplateBtn.onclick = () => {
        navigator.clipboard.writeText(emailBodyText).then(() => {
          firestoreCtx?.showToast?.("Copied email text to clipboard!", "success");
        });
      };
    }
  };

  updateEmailDraft();

  if (saveChottuLinkBtn) {
    saveChottuLinkBtn.onclick = async () => {
      const val = chottuInput ? chottuInput.value.trim() : "";
      if (!val) {
        firestoreCtx?.showToast?.("Please enter a valid ChottuLink URL.", "warning");
        return;
      }
      saveChottuLinkBtn.disabled = true;
      saveChottuLinkBtn.textContent = "Saving…";
      try {
        const { db, doc, setDoc, showToast } = firestoreCtx;
        if (!db || !doc || !setDoc) {
          throw new Error("Firestore context not available for updating lead.");
        }
        await setDoc(doc(db, "waitlist_leads", lead.id), {
          dynamic_link: val,
          chottulink_updated_at: new Date().toISOString()
        }, { merge: true });

        lead.dynamic_link = val;
        showToast?.("Saved ChottuLink to lead! 🎉", "success");
        if (copyChottuLinkBtn) copyChottuLinkBtn.style.display = "inline-flex";
        showChottuSaved(true);
        if (testClaimUrlBtn) testClaimUrlBtn.href = val;
        updateEmailDraft();
        renderLeadsTable();
      } catch (err) {
        console.error("Failed to save Chottu link:", err);
        firestoreCtx?.showToast?.(`Failed to save ChottuLink: ${err.message}`, "error");
      } finally {
        saveChottuLinkBtn.disabled = false;
        saveChottuLinkBtn.textContent = "Save the link";
      }
    };
  }

  // Reset raw json preview state
  if (rawJsonEl) {
    rawJsonEl.textContent = JSON.stringify(lead, null, 2);
    rawJsonEl.style.display = "none";
  }
  if (toggleRawBtn) {
    toggleRawBtn.setAttribute("aria-expanded", "false");
    const label = toggleRawBtn.querySelector("[data-label]");
    if (label) label.textContent = "Raw document";
  }
  if (copyRawBtn) copyRawBtn.style.display = "none";

  // Render Rescued Recipes
  if (recipesListEl) {
    if (urls.length > 0) {
      if (copyRecipeUrlsBtn) copyRecipeUrlsBtn.style.display = "inline-flex";

      recipesListEl.innerHTML = urls.map((url, idx) => {
        let domain = "";
        try {
          domain = new URL(url).hostname.replace(/^www\./, "");
        } catch {
          domain = "link";
        }
        const shownUrl = escapeHTML(url);
        // A link only for http(s): the URL came from the public signup form
        const link = safeUrl(url)
          ? `<a href="${shownUrl}" target="_blank" rel="noopener noreferrer" title="${shownUrl}" class="min-w-0 flex-1 truncate font-mono text-xs text-paprika-700 hover:underline dark:text-paprika-400">${shownUrl}</a>`
          : `<span title="${shownUrl}" class="min-w-0 flex-1 truncate font-mono text-xs text-gray-500 dark:text-gray-400">${shownUrl}</span>`;

        return `
          <li class="flex items-center gap-3 px-3 py-2 text-sm">
            <span class="w-6 flex-none text-right text-xs font-semibold text-gray-400">${idx + 1}.</span>
            <span class="${BADGE.gray} flex-none">${escapeHTML(domain)}</span>
            ${link}
            <button type="button" ${copyAttributes(url, "Copied the recipe link.")} title="Copy the link" class="flex-none rounded-xs p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><span class="sr-only">Copy the link</span>${solidIcon("clipboard-document", "size-4")}</button>
          </li>
        `;
      }).join("");
    } else {
      if (copyRecipeUrlsBtn) copyRecipeUrlsBtn.style.display = "none";
      recipesListEl.innerHTML = `<li class="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">No recipe links came with this signup.</li>`;
    }
  }

  showDialog("leadDetailModal");
}

/**
 * Closes the lead detail modal
 */
export function closeLeadModal() {
  hideDialog("leadDetailModal");
  selectedLeadItem = null;
}

/**
 * Copies all currently filtered lead emails to clipboard
 */
export function copyFilteredEmails() {
  const filtered = getFilteredLeads();
  const emails = filtered
    .map((l) => l.email)
    .filter((e) => Boolean(e && e.includes("@")));

  if (emails.length === 0) {
    firestoreCtx?.showToast?.("No valid emails found in current filter.", "warning");
    return;
  }

  const emailText = emails.join(", ");
  navigator.clipboard.writeText(emailText).then(() => {
    firestoreCtx?.showToast?.(`Copied ${emails.length} email(s) to clipboard!`, "success");
  }).catch((err) => {
    console.error("Clipboard copy failed:", err);
  });
}

/**
 * Exports currently filtered leads as a CSV file
 */
export function exportLeadsCSV() {
  const filtered = getFilteredLeads();
  if (filtered.length === 0) {
    firestoreCtx?.showToast?.("No leads to export in current filter.", "warning");
    return;
  }

  const csvRows = [
    [
      "Email",
      "Date Joined",
      "Campaign Source",
      "Rescued Recipes Count",
      "Discount %",
      "Discount Price USD",
      "Standard Price USD",
      "Status",
      "Claim Status",
      "Claimed At",
      "Claimed By Family ID",
      "Promo Code",
      "Promo Platform",
      "Promo Status",
      "Offer Status",
      "Offer Renews At",
      "Offer Checked At",
      "Phone",
      "Phone Answered At",
      "Newsletter Opt-in",
      "Newsletter Opt-in At",
      "Last Active At",
      "Claim Destination URL",
      "ChottuLink URL",
      "Recipe URLs"
    ].join(",")
  ];

  for (const lead of filtered) {
    const urls = Array.isArray(lead.recipe_urls) ? lead.recipe_urls.join(" | ") : "";
    const claimed = isLeadClaimed(lead);
    const row = [
      escapeCsvField(lead.email || ""),
      escapeCsvField(lead.created_at || ""),
      escapeCsvField(lead.source || ""),
      lead.recipe_count !== undefined ? lead.recipe_count : (Array.isArray(lead.recipe_urls) ? lead.recipe_urls.length : 0),
      lead.discount_percent !== undefined ? lead.discount_percent : 50,
      lead.discount_price_usd !== undefined ? lead.discount_price_usd : 29.99,
      lead.standard_price_usd !== undefined ? lead.standard_price_usd : 59.99,
      escapeCsvField(lead.status || "active"),
      escapeCsvField(claimed ? "claimed" : "unclaimed"),
      escapeCsvField(lead.claimed_at || lead.promo_claimed_at || ""),
      escapeCsvField(lead.claimed_by_family_id || ""),
      escapeCsvField(lead.promo_code || ""),
      escapeCsvField(lead.promo_platform || ""),
      escapeCsvField(lead.promo_status || (claimed ? "claimed" : "")),
      escapeCsvField(lead.offer_status?.state || ""),
      escapeCsvField(lead.offer_status?.renews_at || ""),
      escapeCsvField(lead.offer_status?.checked_at || ""),
      escapeCsvField(lead.phone_platform || ""),
      escapeCsvField(lead.phone_platform_answered_at || ""),
      isNewsletterOptIn(lead) ? "yes" : "no",
      escapeCsvField(isNewsletterOptIn(lead) ? lead.newsletter_opt_in_at || "" : ""),
      escapeCsvField(lead.last_active_at || lead.created_at || ""),
      escapeCsvField(getLeadDestinationUrl(lead)),
      escapeCsvField(lead.dynamic_link || ""),
      escapeCsvField(urls)
    ];
    csvRows.push(row.join(","));
  }

  const csvString = csvRows.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const nowStr = new Date().toISOString().split("T")[0];
  link.setAttribute("href", url);
  link.setAttribute("download", `weeknightbite_waitlist_leads_${nowStr}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  firestoreCtx?.showToast?.(`Exported ${filtered.length} leads to CSV!`, "success");
}

function escapeCsvField(val) {
  const str = String(val).replace(/"/g, '""');
  return `"${str}"`;
}

function escapeHTML(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// A lead's date field as a Date, or null: an ISO string, a Firestore Timestamp or its JSON form
function parseLeadDate(rawDate) {
  if (!rawDate) return null;
  try {
    let d = null;
    if (typeof rawDate.toDate === "function") {
      d = rawDate.toDate();
    } else if (rawDate && typeof rawDate === "object" && typeof rawDate._seconds === "number") {
      d = new Date(rawDate._seconds * 1000 + (rawDate._nanoseconds || 0) / 1e6);
    } else if (typeof rawDate === "string" || typeof rawDate === "number") {
      d = new Date(rawDate);
    }
    return d && !isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

function formatLeadDate(rawDate) {
  const d = parseLeadDate(rawDate);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

// MARK: - Android 50% offer (#1849)

/** What each status reads as, and its badge: red for the members who bought the full price. */
const OFFER_STATUS = {
  offer: { label: "50% offer", badge: BADGE.green },
  regular: { label: "Regular price", badge: BADGE.red },
  household: { label: "Household plan (another member pays)", badge: BADGE.gray },
  other_store: { label: "App Store", badge: BADGE.gray },
  none: { label: "Not subscribed", badge: BADGE.gray },
  unknown: { label: "Couldn't read", badge: BADGE.yellow }
};

function offerDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Asks `adminGetWaitlistOfferStatus` for the leads offered the 50% on Android and lists each account, the ones on the
 * regular price first.
 */
export async function checkAndroidOffers() {
  const btn = document.getElementById("checkAndroidOffersBtn");
  const summary = document.getElementById("androidOffersSummary");
  const wrap = document.getElementById("androidOffersTableWrap");
  const body = document.getElementById("androidOffersTableBody");
  if (!btn || !summary || !wrap || !body) return;
  const { functions, httpsCallable, showToast } = firestoreCtx || {};
  if (!functions || !httpsCallable) return;

  btn.disabled = true;
  btn.textContent = "Checking…";
  try {
    const { data } = await httpsCallable(functions, "adminGetWaitlistOfferStatus")({});
    const rows = [];
    for (const lead of data.leads || []) {
      for (const account of lead.accounts || []) rows.push({ lead, account });
    }
    const rank = { regular: 0, unknown: 1, none: 2, household: 3, other_store: 4, offer: 5 };
    rows.sort((a, b) => (rank[a.account.status] ?? 9) - (rank[b.account.status] ?? 9));

    const counts = data.counts || {};
    summary.textContent = `${(data.leads || []).length} leads, ${rows.length} accounts: ${counts.regular || 0} on the regular price, ${counts.offer || 0} on the 50% offer, ${counts.none || 0} not subscribed.`;
    summary.style.display = "block";
    body.innerHTML = rows.length
      ? rows.map(({ lead, account }) => {
        const status = OFFER_STATUS[account.status] || OFFER_STATUS.unknown;
        const ends = account.status === "none" ? "—" : `${account.is_trial ? "Trial · " : ""}${offerDate(account.expires_at)}`;
        return `
          <tr>
            <td class="py-2 pr-3 text-sm text-gray-900 dark:text-white">${escapeHTML(lead.email || lead.lead_id)}</td>
            <td class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"><code class="font-mono text-xs">${escapeHTML(account.uid)}</code>${account.email && account.email !== lead.email ? `<br>${escapeHTML(account.email)}` : ""}</td>
            <td class="px-3 py-2 text-sm whitespace-nowrap"><span class="${status.badge}">${escapeHTML(status.label)}</span></td>
            <td class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">${escapeHTML(offerText(account.offer_code) || "—")}</td>
            <td class="px-3 py-2 text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">${escapeHTML(ends)}</td>
          </tr>`;
      }).join("")
      : `<tr><td colspan="5" class="py-3 text-sm text-gray-500 dark:text-gray-400">No lead has been offered the 50% on Android yet.</td></tr>`;
    wrap.style.display = "block";
  } catch (err) {
    console.error("❌ Failed to check the Android offers:", err);
    showToast?.(`Couldn't check the Android offers: ${err.message}`, "error");
  } finally {
    btn.disabled = false;
    btn.textContent = "Check";
  }
}

// Re-engagement emails (#2356): the three follow-ups, counted and sent one segment at a time by sendWaitlistReengage.
const REENGAGE_SEGMENT_LABELS = {
  in_app_not_claimed: "Opened the claim link in the app",
  clicked_not_in_app: "Clicked, never reached the app",
  opened_not_clicked: "Opened, didn't click",
  never_opened: "Never opened"
};
const REENGAGE_SKIP_LABELS = {
  claimed: "claimed",
  has_account: "have an account",
  unsubscribed: "unsubscribed",
  suppressed: "bounced or marked as spam",
  no_claim_link: "no claim link",
  no_recipes: "no recipe to claim",
  clicked: "clicked a follow-up",
  not_due: "next step not due yet",
  sending: "being sent",
  done: "got all three"
};
let reengageDue = 0;

/** What a count or a send says, in one paragraph: the segment, who is due which step, and who is left out why. */
export function reengageSummaryText(data) {
  const due = data.due || {};
  const dueTotal = (due[1] || 0) + (due[2] || 0) + (due[3] || 0);
  const skipped = Object.entries(data.skipped || {})
    .sort(([, a], [, b]) => b - a)
    .map(([reason, n]) => `${n} ${REENGAGE_SKIP_LABELS[reason] || reason}`);
  const parts = [
    `${REENGAGE_SEGMENT_LABELS[data.segment] || data.segment}: ${data.total || 0} leads.`,
    dueTotal
      ? `Due now: ${[1, 2, 3].filter((step) => due[step]).map((step) => `step ${step} to ${due[step]}`).join(", ")}.`
      : "Nobody is due a step now.",
    skipped.length ? `Left out: ${skipped.join(", ")}.` : ""
  ];
  if (data.dryRun === false) {
    const failed = data.failed || [];
    parts.unshift(`Sent ${(data.sent || []).length}${failed.length ? `, ${failed.length} not sent` : ""}.`);
    if (data.remaining) parts.push(`${data.remaining} still due: press Send again.`);
  }
  return parts.filter(Boolean).join(" ");
}

function resetReengage() {
  reengageDue = 0;
  const sendBtn = document.getElementById("reengageSendBtn");
  const summary = document.getElementById("reengageSummary");
  if (sendBtn) sendBtn.disabled = true;
  if (summary) summary.style.display = "none";
}

async function callReengage(dryRun) {
  const { functions, httpsCallable } = firestoreCtx || {};
  if (!functions || !httpsCallable) return null;
  const segment = document.getElementById("reengageSegmentSelect")?.value || "";
  const { data } = await httpsCallable(functions, "sendWaitlistReengage")({ segment, dryRun });
  return data;
}

function showReengage(data) {
  const summary = document.getElementById("reengageSummary");
  const sendBtn = document.getElementById("reengageSendBtn");
  reengageDue = data.remaining || 0;
  if (summary) {
    summary.textContent = reengageSummaryText(data);
    summary.style.display = "block";
  }
  if (sendBtn) sendBtn.disabled = reengageDue === 0;
}

/** Count: who in the chosen segment is due which step, and why the rest are left out. Sends nothing. */
export async function countReengage() {
  const btn = document.getElementById("reengageCountBtn");
  if (!btn) return;
  btn.disabled = true;
  btn.textContent = "Counting…";
  try {
    const data = await callReengage(true);
    if (data) showReengage(data);
  } catch (err) {
    console.error("❌ Failed to count the re-engagement segment:", err);
    firestoreCtx?.showToast?.(`Couldn't count the segment: ${err.message}`, "error");
  } finally {
    btn.disabled = false;
    btn.textContent = "Count";
  }
}

/** Send: the due step to the next 40 leads of the counted segment, after a confirm. */
export async function sendReengage() {
  const btn = document.getElementById("reengageSendBtn");
  const segment = document.getElementById("reengageSegmentSelect")?.value || "";
  if (!btn || !reengageDue) return;
  const batch = Math.min(reengageDue, 40);
  if (!window.confirm(`Send the due follow-up to ${batch} lead${batch === 1 ? "" : "s"} in "${REENGAGE_SEGMENT_LABELS[segment] || segment}"?`)) return;
  btn.disabled = true;
  btn.textContent = "Sending…";
  try {
    const data = await callReengage(false);
    if (data) {
      showReengage(data);
      firestoreCtx?.showToast?.(`Sent ${(data.sent || []).length} follow-up email${(data.sent || []).length === 1 ? "" : "s"}.`, "success");
    }
  } catch (err) {
    console.error("❌ Failed to send the re-engagement emails:", err);
    firestoreCtx?.showToast?.(`Couldn't send: ${err.message}`, "error");
  } finally {
    btn.textContent = "Send";
    btn.disabled = reengageDue === 0;
  }
}

/** The drawer's 50% offer section (#1960): the lead's offer_status, and Check with Apple. */
function renderLeadOfferSection(lead) {
  const badge = document.getElementById("modalLeadOfferBadge");
  const details = document.getElementById("modalLeadOfferDetails");
  const checkBtn = document.getElementById("modalCheckOfferBtn");
  if (badge) badge.innerHTML = offerStatusBadgeHTML(lead.offer_status);
  if (details) {
    const rows = offerStatusRows(lead.offer_status);
    details.innerHTML = rows.length
      ? rows.map(([label, value]) => `<dt class="text-gray-500 dark:text-gray-400">${escapeHTML(label)}</dt><dd class="break-all text-gray-900 dark:text-white">${escapeHTML(value)}</dd>`).join("")
      : `<p class="text-sm text-gray-500 sm:col-span-2 dark:text-gray-400">${wasHandedOffer(lead) ? "Not checked yet. The daily sweep fills it in, or check now." : "This lead was never handed the 50%."}</p>`;
  }
  if (checkBtn) {
    checkBtn.disabled = false;
    checkBtn.textContent = "Check with Apple";
    checkBtn.dataset.leadId = lead.id;
    checkBtn.onclick = () => checkLeadOffer(checkBtn.dataset.leadId);
  }
}

/** Asks `adminRefreshLeadOfferStatus` for one lead, and shows the answer in the list and the drawer. */
export async function checkLeadOffer(leadId) {
  const { functions, httpsCallable, showToast } = firestoreCtx || {};
  const checkBtn = document.getElementById("modalCheckOfferBtn");
  if (!functions || !httpsCallable || !leadId) return;
  if (checkBtn) {
    checkBtn.disabled = true;
    checkBtn.textContent = "Checking…";
  }
  try {
    const { data } = await httpsCallable(functions, "adminRefreshLeadOfferStatus")({ lead_id: leadId });
    const status = data?.leads?.[0]?.offer_status || null;
    const lead = leadsList.find((l) => l.id === leadId);
    if (lead) lead.offer_status = status;
    if (selectedLeadItem && selectedLeadItem.id === leadId) {
      selectedLeadItem.offer_status = status;
      renderLeadOfferSection(selectedLeadItem);
    }
    renderLeadsTable();
    showToast?.(status?.error ? `Checked, with an error: ${status.error}` : "Checked the offer.", status?.error ? "error" : "success");
  } catch (err) {
    console.error("❌ Failed to check the lead's offer:", err);
    showToast?.(`Couldn't check the offer: ${err.message}`, "error");
  } finally {
    if (checkBtn) {
      checkBtn.disabled = false;
      checkBtn.textContent = "Check with Apple";
    }
  }
}
